import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [perms, setPerms]     = useState(null); // null = CEO (all), {} = loaded map

  const loadPerms = useCallback(async (role) => {
    if (role === 'ceo') { setPerms(null); return; }
    try {
      const r = await fetch('/api/auth/permissions', { credentials: 'include' });
      const d = await r.json();
      if (d.success) setPerms(d.data || {});
    } catch { setPerms({}); }
  }, []);

  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setUser(d.user);
          loadPerms(d.user.role);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [loadPerms]);

  async function login(email, password) {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (data.success) {
      setUser(data.user);
      await loadPerms(data.user.role);
      return { success: true };
    }
    return { success: false, error: data.error };
  }

  async function requestOtp(email) {
    const res = await fetch('/api/auth/request-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    if (!res.ok) return { success: false, error: data.error || 'Something went wrong.' };
    return { success: true };
  }

  async function verifyOtp(email, code) {
    const res = await fetch('/api/auth/verify-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, code }),
    });
    const data = await res.json();
    if (!res.ok) return { success: false, error: data.error || 'Invalid or expired code.' };
    setUser(data.user);
    await loadPerms(data.user.role);
    return { success: true };
  }

  async function logout() {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setUser(null);
    setPerms(null);
  }

  const isCEO            = user?.role === 'ceo';
  const isCOO            = user?.role === 'coo';
  const isSalesManager   = user?.role === 'sales_manager';
  const isAccountManager = user?.role === 'account_manager';
  const isExecutionTeam  = user?.role === 'execution_team';
  const isReviewer       = user?.role === 'reviewer';

  // hasPerm: CEO always returns true; others check loaded permissions map
  function hasPerm(permission) {
    if (!user) return false;
    if (isCEO) return true;
    if (perms === null) return false; // permissions not yet loaded — deny until confirmed
    return perms[permission] === true;
  }

  // Legacy helpers — preserved for backward compatibility, now driven by permissions
  const canWrite       = hasPerm('creators.edit') || hasPerm('campaigns.edit');
  const canImport      = hasPerm('creators.import');
  const canManageUsers = isCEO;

  return (
    <AuthContext.Provider value={{
      user, loading, login, requestOtp, verifyOtp, logout,
      isCEO, isCOO, isSalesManager, isAccountManager, isExecutionTeam, isReviewer,
      canWrite, canImport, canManageUsers,
      hasPerm,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
